import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowDownward, ArrowUpward, AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { Box, Link, Stack, Typography } from '@mui/material';
import Button from 'domains/core/components/Button';
import { BrandProfileField, BrandProfilePayload } from 'domains/brand/types';
import { URLPaths } from 'models/enums';
import { calculatePercentage, getStatusCopy } from './brandLearningStrengthUtils';
import BrandLearningStrengthChecklist from './BrandLearningStrengthChecklist';

export type BrandLearningStrengthCardMode = 'landing' | 'edit';

type BrandLearningStrengthCardProps =
    | { mode: 'landing'; brandProfile: BrandProfileField[] }
    | { mode: 'edit'; brandProfile: BrandProfileField[]; formValues: BrandProfilePayload };

const BrandLearningStrengthCard = (props: BrandLearningStrengthCardProps) => {
    const history = useHistory();
    const [isChecklistExpanded, setIsChecklistExpanded] = useState(false);

    const formValues = props.mode === 'edit' ? props.formValues : undefined;
    const percentage = calculatePercentage(props.brandProfile, formValues);
    const isLanding = props.mode === 'landing';
    const showAddMoreDetails = isLanding && percentage < 100;
    const progressBarColor = 'teal';
    const { status: statusMessage, nextStep } = getStatusCopy(props.brandProfile, formValues);

    return (
        <Stack
            spacing={2}
            sx={{
                p: 3,
                borderRadius: '16px',
                border: '0 solid rgba(34, 70, 73, 0.50)',
                background: 'linear-gradient(140deg, rgba(255, 194, 8, 0.09) 13.55%, rgba(232, 31, 118, 0.07) 83.77%)',
                width: 420,
                minWidth: 420,
                maxWidth: 420,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                <AutoAwesomeIcon color="error" fontSize="medium" />
                <Typography variant="h6" fontWeight={500} sx={{ textTransform: 'lowercase' }}>
                    brand learning strength
                </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={isLanding ? 0 : 2}>
                <Box
                    sx={{
                        flex: 1,
                        height: 8,
                        backgroundColor: 'grey.300',
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            width: `${percentage}%`,
                            backgroundColor: progressBarColor,
                            transition: 'width 0.3s ease',
                        }}
                    />
                </Box>
                {!isLanding && (
                    <Typography variant="body2" fontWeight={600}>
                        {String(percentage).padStart(2, '0')}%
                    </Typography>
                )}
            </Stack>

            <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                    {statusMessage}
                </Typography>
                {nextStep && (
                    <Typography variant="body2" color="text.secondary">
                        <Box component="span" fontWeight={600}>
                            Next:{' '}
                        </Box>
                        {nextStep}
                    </Typography>
                )}
            </Stack>

            <Stack spacing={0.5}>
                <Stack alignItems="center" direction="row" flexWrap="wrap" spacing={2} useFlexGap>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => setIsChecklistExpanded((prev) => !prev)}
                        sx={{
                            color: 'info.main',
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '& .MuiSvgIcon-root': { color: 'inherit' },
                        }}
                    >
                        how this is calculated
                        {isChecklistExpanded ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
                    </Link>
                    {showAddMoreDetails ? (
                        <Button
                            aria-label="Add more details, go to brand setup edit"
                            color="primary"
                            data-testid="brand-learning-strength-add-more-details-button"
                            size="small"
                            variant="contained"
                            onClick={() => history.push(URLPaths.SETTINGS_BRAND_EDIT)}
                        >
                            Add more details
                        </Button>
                    ) : null}
                </Stack>

                {isChecklistExpanded && (
                    <BrandLearningStrengthChecklist brandProfile={props.brandProfile} formValues={formValues} />
                )}
            </Stack>
        </Stack>
    );
};

export default BrandLearningStrengthCard;
